import { useState, useCallback, useRef } from 'react';

export const DEFAULT_TIMEOUT = 30000; // 30 seconds

export const useComponentLoading = (initialStates = {}) => {
    const [loadingStates, setLoadingStates] = useState(initialStates);
    const [loadingMessages, setLoadingMessages] = useState({});
    const [loadingProgress, setLoadingProgress] = useState({});
    const timeoutRefs = useRef({});
    const loadingQueue = useRef([]);
    
    // Clear any existing timeout for the loading key
    const clearTimeout = useCallback((key) => {
        if (timeoutRefs.current[key]) {
            window.clearTimeout(timeoutRefs.current[key]);
            delete timeoutRefs.current[key];
        }
    }, []);

    const setLoading = useCallback((key, isLoading, message = '', timeout = DEFAULT_TIMEOUT) => {
        clearTimeout(key);
        
        setLoadingStates(prev => ({
            ...prev,
            [key]: isLoading
        }));
        
        setLoadingMessages(prev => ({
            ...prev,
            [key]: message
        }));

        if (isLoading && timeout > 0) {
            timeoutRefs.current[key] = window.setTimeout(() => {
                console.warn(`Loading timeout for ${key} after ${timeout}ms`);
                setLoading(key, false);
            }, timeout);
        }
    }, [clearTimeout]);

    const updateProgress = useCallback((key, progress) => {
        setLoadingProgress(prev => ({
            ...prev,
            [key]: Math.min(Math.max(0, progress), 100) // Ensure progress is between 0-100
        }));
    }, []);

    const startLoading = useCallback((key, message = '', timeout = DEFAULT_TIMEOUT) => {
        setLoading(key, true, message, timeout);
        updateProgress(key, 0);
    }, [setLoading, updateProgress]);

    const stopLoading = useCallback((key) => {
        clearTimeout(key);
        setLoading(key, false, '');
        updateProgress(key, 100);
        
        // Process next item in queue if any
        const nextInQueue = loadingQueue.current.shift();
        if (nextInQueue) {
            const { key: nextKey, fn, message, timeout } = nextInQueue;
            withLoading(nextKey, fn, message, timeout);
        }
    }, [clearTimeout, setLoading, updateProgress]);

    // Enhanced withLoading that supports progress updates
    const withLoading = useCallback(async (
        key, 
        asyncFunction,
        message = '',
        timeout = DEFAULT_TIMEOUT,
        options = {}
    ) => {
        const { 
            queue = false,  // Whether to queue this operation if another is in progress
            onProgress     // Optional progress callback
        } = options;

        // If queuing is enabled and something is loading, add to queue
        if (queue && Object.values(loadingStates).some(state => state)) {
            return new Promise((resolve, reject) => {
                loadingQueue.current.push({ 
                    key, 
                    fn: async () => {
                        try {
                            const result = await asyncFunction();
                            resolve(result);
                        } catch (error) {
                            reject(error);
                        }
                    },
                    message,
                    timeout
                });
            });
        }

        try {
            startLoading(key, message, timeout);
            
            // Create a progress callback that the async function can use
            const progressCallback = (progress, message) => {
                if (typeof progress === 'number') {
                    updateProgress(key, progress);
                }
                if (message) {
                    setLoadingMessages(prev => ({
                        ...prev,
                        [key]: message
                    }));
                }
            };
            
            // Pass the progress callback to the async function
            const result = await asyncFunction(progressCallback);
                
            return result;
        } catch (error) {
            console.error(`Error in loading operation ${key}:`, error);
            throw error;
        } finally {
            stopLoading(key);
        }
    }, [loadingStates, startLoading, stopLoading, updateProgress]);

    const reset = useCallback(() => {
        Object.keys(timeoutRefs.current).forEach(clearTimeout);
        loadingQueue.current = [];
        setLoadingStates({});
        setLoadingMessages({});
        setLoadingProgress({});
    }, [clearTimeout]);

    return {
        loadingStates,
        loadingMessages,
        loadingProgress,
        setLoading,
        startLoading,
        stopLoading,
        withLoading,
        updateProgress,
        reset,
        isLoading: (key) => loadingStates[key] || false,
        getMessage: (key) => loadingMessages[key] || '',
        getProgress: (key) => loadingProgress[key] || 0,
        isAnyLoading: () => Object.values(loadingStates).some(state => state),
        getActiveLoadingKeys: () => Object.entries(loadingStates)
            .filter(([_, value]) => value)
            .map(([key]) => key)
    };
}; 